import HighchartsReact from 'highcharts-react-official';
import { MapType } from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { ReactElement, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsiveHighcharts } from 'widgets/ChartWidgets/ChartWidget.hooks';
import { DataTableChartMenuSettings } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommonMenuSettings';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { HighchartsMapWrapper } from './HighchartsMapWrapper';
import { MapWidgetData } from './MapWidget.types';
import { MapWidgetToolbar } from './MapWidgetToolbar';
import { LabelSettings, LegendSettings, MapSettings } from './Settings';

export const MapWidget = (): ReactElement => {
  const { heightPx, widthPx, mapType } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'mapChartToolbar' });
  const { openWidgetSideMenu } = useEditor();

  const mapRef = useRef<HighchartsReact.RefObject>(null);

  const { customOnResize } = useResponsiveHighcharts({ chartRef: mapRef, widthPx, heightPx });

  return (
    <WidgetBase onResize={customOnResize} onDoubleClick={openWidgetSideMenu}>
      <WidgetSideMenuSettings>
        <DataTableChartMenuSettings
          alternateTitle={t('editMap.label')}
          disableAddColumn
          showMapType
          headerRowNumber={0}
          headerColumnNumber={0}
          dataTableProps={{
            minCols: 2,
            minRows: 1,
            minSpareCols: 0,
            minSpareRows: 0,
            // Todo: i18n
            colHeaders: ['Name', 'Value'],
          }}
          customSetupTab={
            <>
              <MapSettings />
              <LabelSettings />
              {mapType === MapType.Choropleth && <LegendSettings />}
            </>
          }
        />
      </WidgetSideMenuSettings>
      <WidgetToolbar>
        <MapWidgetToolbar />
      </WidgetToolbar>
      <MapChart ref={mapRef} />
    </WidgetBase>
  );
};

export const ReadOnlyMapWidget = ({ includeAltTextImg }: { includeAltTextImg?: boolean }): ReactElement => {
  return (
    <ReadOnlyWidgetBase disableSingleSelect>
      <MapChart />
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const MapChart = forwardRef<HighchartsReact.RefObject>(
  (_, mapRef): ReactElement => <HighchartsMapWrapper mapRef={mapRef} />,
);
MapChart.displayName = 'MapChart';
